import { RefObject, useEffect, useRef, useState } from "react";
import { Message } from "../types";

type UseChatScrollBehaviorParams = {
  messages: Message[];
  streamingContent: string;
  isLoading: boolean;
  messagesEndRef: RefObject<HTMLDivElement>;
};

/** Identifies the thread's tail: changes when a message is added or a thread is
 *  loaded, not when a message already on screen updates itself in place. */
const tailKey = (messages: Message[]) => `${messages.length}:${messages[messages.length - 1]?.id ?? ""}`;

export function useChatScrollBehavior({
  messages,
  streamingContent,
  isLoading,
  messagesEndRef,
}: UseChatScrollBehaviorParams): number {
  const [lastUserMessageIndex, setLastUserMessageIndex] = useState<number>(-1);

  useEffect(() => {
    const lastUserIdx =
      messages.length - 1 - [...messages].reverse().findIndex((m) => m.role === "user");
    if (lastUserIdx >= 0) {
      setLastUserMessageIndex(lastUserIdx);
    }
  }, [messages]);

  // Follow the conversation as it grows, never in-place updates: an interactive
  // card (e.g. the Business Profile fixes) rewrites its own message on every
  // click, and jumping to the bottom then pulls the card out from under the owner.
  const tail = tailKey(messages);
  const prevTail = useRef(tail);
  useEffect(() => {
    const grew = prevTail.current !== tail;
    prevTail.current = tail;
    if (messages.length === 0) return;
    if (grew || isLoading || streamingContent) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [tail, isLoading, streamingContent, messages.length, messagesEndRef]);

  return lastUserMessageIndex;
}
